import React from "react";
import type { PullPlanKind, PullPlanMode } from "../../../../models";
import type { SectionCardSummary } from "../summarizeSection";
import "./SectionCard.css";

interface Props {
  summary: SectionCardSummary;
  latestDraftId: number | null;
  /** "plain" renders the non-Takt variant (dashed, plan-only flag, Adjust-only
   *  — never a Re-pull button). Absent or "takt" renders a normal Takt section
   *  card with both forks. */
  kind?: PullPlanKind;
  onStartPull: (mode: PullPlanMode) => void;
  onContinuePull: (draftId: number) => void;
  /** Throw away the in-progress draft (only rendered when one exists). */
  onDiscardDraft?: (draftId: number) => void;
}

const fmtDate = (iso: string | null): string | null => {
  if (!iso) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return m ? `${parseInt(m[2], 10)}/${parseInt(m[3], 10)}` : null;
};

const SectionCard: React.FC<Props> = ({
  summary,
  latestDraftId,
  kind,
  onStartPull,
  onContinuePull,
  onDiscardDraft,
}) => {
  const [open, setOpen] = React.useState(false);
  const isPlain = kind === "plain";
  const start = fmtDate(summary.earliestStart);
  const finish = fmtDate(summary.latestFinish);
  const dateRange = start && finish ? `${start} → ${finish}` : null;
  const areas = summary.wbsNodes.length;
  const members = summary.scopeMemberCount;
  const tagged = summary.taggedCount;
  const untagged = summary.untaggedCount;
  const clean = untagged === 0 && tagged > 0;
  const total = Math.max(tagged + untagged, 1);
  const glyph = clean ? "◉" : untagged > tagged ? "◔" : "◑";

  return (
    <div
      className={`pull-plan-section-card${isPlain ? " pull-plan-section-card--plain" : ""}`}
    >
      <div className="pull-plan-section-card-head">
        <div className="pull-plan-section-card-title">
          {summary.taktA && (
            <span className="pull-plan-section-card-takt-a">{summary.taktA}</span>
          )}
          <span className="pull-plan-section-card-name">{summary.name}</span>
          {latestDraftId != null && (
            <span className="pull-plan-section-card-draft-chip">PULL DRAFT</span>
          )}
          {isPlain && (
            <span className="pull-plan-section-card-notakt-flag">
              ⚙ plan only · not Takt
            </span>
          )}
          {summary.scopeOverlaps.length > 0 && (
            <span
              className="pull-plan-section-card-overlap-chip"
              title={summary.scopeOverlaps
                .map(
                  (o) =>
                    `Shares ${o.shared_wbs_ids.length} leaf${o.shared_wbs_ids.length === 1 ? "" : "s"} with “${o.name}”`,
                )
                .join("\n")}
            >
              ⚠ shares a leaf
            </span>
          )}
        </div>

        {(summary.wbsRoot || dateRange) && (
          <div className="pull-plan-section-card-sub">
            {summary.wbsRoot ?? ""}
            {summary.wbsRoot && dateRange ? " — " : ""}
            {dateRange ?? ""}
          </div>
        )}

        {members > 0 && (
          <button
            type="button"
            className={`pull-plan-section-card-scope-chip${clean ? " is-clean" : ""}`}
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
          >
            {glyph} {areas} area{areas === 1 ? "" : "s"} · {members} activit
            {members === 1 ? "y" : "ies"}
            {clean ? " · clean" : ""}{" "}
            <span className="pull-plan-section-card-scope-caret">{open ? "▴" : "▾"}</span>
          </button>
        )}
      </div>

      {summary.wbsNodes.length > 0 && (
        <div className="pull-plan-section-card-leaves">
          {summary.wbsNodes.slice(0, 6).map((n) => (
            <span className="pull-plan-section-card-leaf-chip" key={n.id}>
              {n.name ?? `#${n.id}`}
            </span>
          ))}
          {summary.wbsNodes.length > 6 && (
            <span className="pull-plan-section-card-leaf-chip">
              +{summary.wbsNodes.length - 6}
            </span>
          )}
        </div>
      )}

      {open && members > 0 && (
        <div className="pull-plan-section-card-break">
          <div className="pull-plan-section-card-bar">
            {tagged > 0 && (
              <span
                className="pp-sb-own"
                style={{ width: `${(tagged / total) * 100}%` }}
              />
            )}
            {untagged > 0 && (
              <span
                className="pp-sb-untag"
                style={{ width: `${(untagged / total) * 100}%` }}
              />
            )}
          </div>
          <div className="pull-plan-section-card-legend">
            <span className="pp-leg">
              <span className="pp-dot pp-dot-own" /> {tagged} tagged
            </span>
            <span className="pp-leg">
              <span className="pp-dot pp-dot-untag" /> {untagged} not yet tagged
            </span>
          </div>
          {untagged > 0 && (
            <div className="pull-plan-section-card-break-note">
              {isPlain
                ? "Sequence-only — these stay untagged."
                : `${untagged} activities in these areas aren't tagged "${summary.name}" yet; a Re-pull pulls them in.`}
            </div>
          )}
        </div>
      )}

      <div className="pull-plan-section-card-actions">
        {latestDraftId != null ? (
          <>
            <button
              type="button"
              className="pull-plan-section-card-btn pull-plan-section-card-btn--primary"
              onClick={() => onContinuePull(latestDraftId)}
            >
              ↺ Resume draft
            </button>
            {onDiscardDraft && (
              <button
                type="button"
                className="pull-plan-section-card-btn pull-plan-section-card-btn--ghost"
                title="Throw away the draft and start over"
                onClick={() => onDiscardDraft(latestDraftId)}
              >
                Discard
              </button>
            )}
          </>
        ) : (
          <>
            <button
              type="button"
              className="pull-plan-section-card-btn pull-plan-section-card-btn--adjust"
              onClick={() => onStartPull("adjust")}
            >
              Plan
            </button>
            {!isPlain && (
              <button
                type="button"
                className="pull-plan-section-card-btn pull-plan-section-card-btn--primary"
                onClick={() => onStartPull("repull")}
              >
                ⟲ Re-pull
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default SectionCard;
